/* Half-Life-Modelle (.mdl, Studio-Format Version 10): lesen und als fertige Dreiecke in einer Stellung liefern. Keine DOM-Abhängigkeit.
 * Aufbau nach dem Format (Header, Knochen, Körperteile, Teilmodelle, Netze mit Dreiecksbefehlen, Texturen mit 256er-Palette, Animationsdaten).
 * Modelle ohne Texturen (Datei name.mdl) nehmen sie aus der Begleitdatei nameT.mdl; Animationen in Sequenzgruppen > 0 stehen in
 * Zusatzdateien (name01.mdl …) und werden bei Bedarf mitgegeben. Gezeigt wird eine feste Stellung (Sequenz, Bild), keine Bewegung. */

/** Fehler beim Lesen einer MDL-Datei; code erlaubt dem Aufrufer eine eigene (übersetzte) Meldung */
export type MdlErrorCode = 'short' | 'not_mdl' | 'version' | 'range';
export class MdlError extends Error {
  code: MdlErrorCode;
  /** bei 'version': die gefundene Version */
  detail?: number;
  constructor(code: MdlErrorCode, message: string, detail?: number) { super(message); this.name = 'MdlError'; this.code = code; this.detail = detail; }
}
const E = {
  short: (): MdlError => new MdlError('short', 'Not an MDL file: too short'),
  notMdl: (): MdlError => new MdlError('not_mdl', 'Not an MDL file (IDST id missing)'),
  version: (v: number): MdlError => new MdlError('version', 'MDL version ' + v + ' is not supported (expected 10)', v),
  range: (): MdlError => new MdlError('range', 'MDL file damaged: data outside the file')
};
const ID_IDST = 0x54534449;   // "IDST"
const VERSION = 10;

export interface MdlBone { name: string; parent: number; value: number[]; scale: number[]; controller: number[] }
export interface MdlController { bone: number; type: number; start: number; end: number; rest: number; index: number }
export interface MdlTexture { name: string; flags: number; w: number; h: number; rgba: Uint8ClampedArray }
export interface MdlMesh { skinref: number; cmds: Int16Array }
export interface MdlSubmodel {
  name: string; verts: Float32Array; vertBone: Uint8Array; norms: Float32Array; normBone: Uint8Array; meshes: MdlMesh[];
}
export interface MdlBodypart { name: string; base: number; models: MdlSubmodel[] }
export interface MdlSequence {
  label: string; fps: number; flags: number; numframes: number; motiontype: number; motionbone: number; numblends: number;
  animindex: number; group: number;
}
export interface MdlFile {
  name: string; flags: number; eye: number[]; min: number[]; max: number[];
  bones: MdlBone[]; controllers: MdlController[]; bodyparts: MdlBodypart[]; sequences: MdlSequence[]; seqGroups: { label: string; name: string }[];
  textures: MdlTexture[];
  /** Skin-Familien: je Familie die Textur-Nummer je skinref */
  skins: Int16Array[]; numskinref: number;
  /** Rohdaten für die Animationsdaten der Gruppe 0 */
  buf: ArrayBuffer;
  /** true, wenn die Datei selbst keine Texturen hat (sie stehen in der Begleitdatei nameT.mdl) */
  needsTextureFile: boolean;
}

const cstr = (u8: Uint8Array, off: number, len: number): string => {
  let e = off;
  while (e < off + len && u8[e] !== 0) e++;
  let s = '';
  for (let i = off; i < e; i++) s += String.fromCharCode(u8[i]);
  return s;
};

/** Modell lesen. texBuf: Inhalt der Begleitdatei nameT.mdl für Modelle ohne eigene Texturen. */
export function parseMdl(buf: ArrayBuffer, texBuf?: ArrayBuffer): MdlFile {
  if (buf.byteLength < 244) throw E.short();
  const dv = new DataView(buf), u8 = new Uint8Array(buf);
  if (dv.getUint32(0, true) !== ID_IDST) throw E.notMdl();
  const ver = dv.getInt32(4, true);
  if (ver !== VERSION) throw E.version(ver);
  const i32 = (o: number): number => dv.getInt32(o, true), f32 = (o: number): number => dv.getFloat32(o, true);
  const vec = (o: number): number[] => [f32(o), f32(o + 4), f32(o + 8)];
  const need = (off: number, len: number): void => { if (off < 0 || len < 0 || off + len > buf.byteLength) throw E.range(); };

  const numbones = i32(140), boneindex = i32(144), numctl = i32(148), ctlindex = i32(152), numseq = i32(164), seqindex = i32(168);
  const numgroups = i32(172), groupindex = i32(176), numtex = i32(180), texindex = i32(184), numskinref = i32(192), numfam = i32(196), skinindex = i32(200);
  const numbp = i32(204), bpindex = i32(208);
  need(boneindex, numbones * 112); need(ctlindex, numctl * 24); need(seqindex, numseq * 176); need(groupindex, numgroups * 104); need(bpindex, numbp * 76);

  const bones: MdlBone[] = [];
  for (let i = 0; i < numbones; i++) {
    const o = boneindex + i * 112;
    const arr = (p: number, isInt: boolean): number[] => Array.from({ length: 6 }, (_x, k) => (isInt ? i32(p + k * 4) : f32(p + k * 4)));
    bones.push({ name: cstr(u8, o, 32), parent: i32(o + 32), controller: arr(o + 40, true), value: arr(o + 64, false), scale: arr(o + 88, false) });
  }
  const controllers: MdlController[] = [];
  for (let i = 0; i < numctl; i++) {
    const o = ctlindex + i * 24;
    controllers.push({ bone: i32(o), type: i32(o + 4), start: f32(o + 8), end: f32(o + 12), rest: i32(o + 16), index: i32(o + 20) });
  }
  const sequences: MdlSequence[] = [];
  for (let i = 0; i < numseq; i++) {
    const o = seqindex + i * 176;
    sequences.push({ label: cstr(u8, o, 32), fps: f32(o + 32), flags: i32(o + 36), numframes: i32(o + 56), motiontype: i32(o + 72), motionbone: i32(o + 76),
      numblends: i32(o + 128), animindex: i32(o + 132), group: i32(o + 164) });
  }
  const seqGroups: { label: string; name: string }[] = [];
  for (let i = 0; i < numgroups; i++) { const o = groupindex + i * 104; seqGroups.push({ label: cstr(u8, o, 32), name: cstr(u8, o + 32, 64) }); }

  // Körperteile → Teilmodelle → Netze
  const bodyparts: MdlBodypart[] = [];
  for (let b = 0; b < numbp; b++) {
    const o = bpindex + b * 76, nummodels = i32(o + 64), base = i32(o + 68), modelindex = i32(o + 72);
    need(modelindex, nummodels * 112);
    const models: MdlSubmodel[] = [];
    for (let m = 0; m < nummodels; m++) {
      const mo = modelindex + m * 112;
      const nummesh = i32(mo + 72), meshindex = i32(mo + 76), numverts = i32(mo + 80), vinfo = i32(mo + 84), vindex = i32(mo + 88);
      const numnorms = i32(mo + 92), ninfo = i32(mo + 96), nindex = i32(mo + 100);
      need(vindex, numverts * 12); need(vinfo, numverts); need(nindex, numnorms * 12); need(ninfo, numnorms); need(meshindex, nummesh * 20);
      const verts = new Float32Array(numverts * 3), norms = new Float32Array(numnorms * 3);
      for (let k = 0; k < verts.length; k++) verts[k] = f32(vindex + k * 4);
      for (let k = 0; k < norms.length; k++) norms[k] = f32(nindex + k * 4);
      const meshes: MdlMesh[] = [];
      for (let k = 0; k < nummesh; k++) {
        const eo = meshindex + k * 20, triindex = i32(eo + 4), skinref = i32(eo + 8);
        // Dreiecksbefehle: Zahl n (> 0 Streifen, < 0 Fächer) und n × 4 Werte (Punkt, Normale, s, t), Ende mit 0
        need(triindex, 2);
        let p = triindex;
        for (;;) {
          need(p, 2);
          const n = dv.getInt16(p, true); p += 2;
          if (n === 0) break;
          p += Math.abs(n) * 8;
        }
        meshes.push({ skinref, cmds: new Int16Array(Array.from({ length: (p - triindex) / 2 }, (_x, q) => dv.getInt16(triindex + q * 2, true))) });
      }
      models.push({ name: cstr(u8, mo, 64), verts, vertBone: u8.slice(vinfo, vinfo + numverts), norms, normBone: u8.slice(ninfo, ninfo + numnorms), meshes });
    }
    bodyparts.push({ name: cstr(u8, o, 64), base, models });
  }

  // Texturen und Skins: aus der Datei selbst oder aus der Begleitdatei
  const needsTextureFile = numtex === 0;
  let tdv = dv, tu8 = u8, tnumtex = numtex, ttexindex = texindex, tskinref = numskinref, tfam = numfam, tskinindex = skinindex;
  if (needsTextureFile && texBuf) {
    if (texBuf.byteLength < 244) throw E.short();
    tdv = new DataView(texBuf); tu8 = new Uint8Array(texBuf);
    if (tdv.getUint32(0, true) !== ID_IDST || tdv.getInt32(4, true) !== VERSION) throw E.notMdl();
    tnumtex = tdv.getInt32(180, true); ttexindex = tdv.getInt32(184, true); tskinref = tdv.getInt32(192, true); tfam = tdv.getInt32(196, true); tskinindex = tdv.getInt32(200, true);
  }
  const tbuflen = tu8.byteLength;
  const textures: MdlTexture[] = [];
  if (tnumtex > 0 && ttexindex >= 0 && ttexindex + tnumtex * 80 <= tbuflen) {
    for (let i = 0; i < tnumtex; i++) {
      const o = ttexindex + i * 80, flags = tdv.getInt32(o + 64, true), w = tdv.getInt32(o + 68, true), h = tdv.getInt32(o + 72, true), idx = tdv.getInt32(o + 76, true);
      const rgba = new Uint8ClampedArray(Math.max(0, w) * Math.max(0, h) * 4);
      if (w > 0 && h > 0 && idx >= 0 && idx + w * h + 768 <= tbuflen) {
        const pal = idx + w * h, masked = (flags & 0x40) !== 0;
        for (let k = 0; k < w * h; k++) {
          const c = tu8[idx + k], q = k * 4;
          rgba[q] = tu8[pal + c * 3]; rgba[q + 1] = tu8[pal + c * 3 + 1]; rgba[q + 2] = tu8[pal + c * 3 + 2];
          rgba[q + 3] = masked && c === 255 ? 0 : 255;
        }
      }
      textures.push({ name: cstr(tu8, o, 64), flags, w: Math.max(0, w), h: Math.max(0, h), rgba });
    }
  }
  const skins: Int16Array[] = [];
  if (tfam > 0 && tskinref > 0 && tskinindex >= 0 && tskinindex + tfam * tskinref * 2 <= tbuflen) {
    for (let f = 0; f < tfam; f++) skins.push(new Int16Array(Array.from({ length: tskinref }, (_x, k) => tdv.getInt16(tskinindex + (f * tskinref + k) * 2, true))));
  }

  return { name: cstr(u8, 8, 64), flags: i32(136), eye: vec(76), min: vec(88), max: vec(100), bones, controllers, bodyparts, sequences, seqGroups,
    textures, skins, numskinref: tskinref, buf, needsTextureFile };
}

// ---- Stellung

type Mat = Float32Array;   // 3 × 4 zeilenweise

function angleQuat(a: number[]): number[] {
  const sy = Math.sin(a[2] * 0.5), cy = Math.cos(a[2] * 0.5), sp = Math.sin(a[1] * 0.5), cp = Math.cos(a[1] * 0.5), sr = Math.sin(a[0] * 0.5), cr = Math.cos(a[0] * 0.5);
  return [sr * cp * cy - cr * sp * sy, cr * sp * cy + sr * cp * sy, cr * cp * sy - sr * sp * cy, cr * cp * cy + sr * sp * sy];
}

function quatMat(q: number[], pos: number[]): Mat {
  const [x, y, z, w] = q, m = new Float32Array(12);
  m[0] = 1 - 2 * y * y - 2 * z * z; m[4] = 2 * x * y + 2 * w * z; m[8] = 2 * x * z - 2 * w * y;
  m[1] = 2 * x * y - 2 * w * z; m[5] = 1 - 2 * x * x - 2 * z * z; m[9] = 2 * y * z + 2 * w * x;
  m[2] = 2 * x * z + 2 * w * y; m[6] = 2 * y * z - 2 * w * x; m[10] = 1 - 2 * x * x - 2 * y * y;
  m[3] = pos[0]; m[7] = pos[1]; m[11] = pos[2];
  return m;
}

function concat(a: Mat, b: Mat): Mat {
  const r = new Float32Array(12);
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) r[i * 4 + j] = a[i * 4] * b[j] + a[i * 4 + 1] * b[4 + j] + a[i * 4 + 2] * b[8 + j];
    r[i * 4 + 3] = a[i * 4] * b[3] + a[i * 4 + 1] * b[7] + a[i * 4 + 2] * b[11] + a[i * 4 + 3];
  }
  return r;
}

/** Wert aus den lauflängenkodierten Animationsdaten: at = Byte-Lage der Liste, k = Bild */
function animValue(dv: DataView, at: number, frame: number): number {
  let p = at, k = frame;
  const len = dv.byteLength;
  while (p + 2 <= len && dv.getUint8(p + 1) <= k) {          // total
    const total = dv.getUint8(p + 1);
    if (total === 0) return 0;                                 // beschädigt: sonst Endlosschleife
    k -= total; p += (dv.getUint8(p) + 1) * 2;                 // valid + 1 Einträge weiter
  }
  if (p + 2 > len) return 0;
  const valid = dv.getUint8(p);
  const idx = valid > k ? k + 1 : valid;
  const q = p + idx * 2;
  return q + 2 <= len ? dv.getInt16(q, true) : 0;
}

/** Knochenmatrizen (3 × 4 je Knochen) für Sequenz und Bild. controllers: Wert je Regler 0..255 (Standard: Ruhewert).
 *  seqFiles: Inhalt der Zusatzdateien der Sequenzgruppen (Index = Gruppe), nur nötig für Gruppen > 0. Fehlen Animationsdaten,
 *  gilt die Grundstellung der Knochen. */
export function bonePose(m: MdlFile, seq = 0, frame = 0, seqFiles: (ArrayBuffer | undefined)[] = []): Mat[] {
  const adj = m.controllers.map((c) => {
    let v: number;
    if (c.index <= 3) {
      if (c.type & 0x8000) v = c.rest * (360 / 256) + c.start;
      else { const t = Math.max(0, Math.min(1, c.rest / 255)); v = (1 - t) * c.start + t * c.end; }
    } else v = c.start;   // Mund geschlossen
    const type = c.type & 0x7fff;
    return type === 0x0008 || type === 0x0010 || type === 0x0020 ? (v * Math.PI) / 180 : v;
  });
  const sd = m.sequences[seq] || m.sequences[0];
  let adv: DataView | null = null, base = 0;
  if (sd && sd.numblends > 0 && m.bones.length) {
    if (sd.group === 0) { adv = new DataView(m.buf); base = sd.animindex; }
    else { const f = seqFiles[sd.group]; if (f) { adv = new DataView(f); base = sd.animindex; } }
  }
  const f = sd ? Math.max(0, Math.min(Math.floor(frame), Math.max(0, sd.numframes - 1))) : 0;
  const out: Mat[] = [];
  m.bones.forEach((b, i) => {
    const pos = b.value.slice(0, 3), ang = b.value.slice(3, 6);
    const ao = adv ? base + i * 12 : -1;
    for (let j = 0; j < 6; j++) {
      const off = adv && ao + 12 <= adv.byteLength ? adv.getUint16(ao + j * 2, true) : 0;
      let v = b.value[j];
      if (adv && off) v += animValue(adv, ao + off, f) * b.scale[j];
      if (b.controller[j] >= 0 && b.controller[j] < adj.length) v += adj[b.controller[j]];
      if (j < 3) pos[j] = v; else ang[j - 3] = v;
    }
    if (sd && i === sd.motionbone) {   // Bewegung der Sequenz nicht in die Stellung einrechnen
      if (sd.motiontype & 1) pos[0] = 0;
      if (sd.motiontype & 2) pos[1] = 0;
      if (sd.motiontype & 4) pos[2] = 0;
    }
    const local = quatMat(angleQuat(ang), pos);
    out.push(b.parent >= 0 && b.parent < i ? concat(out[b.parent], local) : local);
  });
  return out;
}

// ---- Geometrie

/** Dreiecke einer Textur: Lage, Normale, Textur-Koordinaten (s/Breite, t/Höhe), alles flach */
export interface MdlGroup { texture: number; pos: number[]; nrm: number[]; uv: number[] }

export interface BuildOpts { body?: number; skin?: number; seq?: number; frame?: number; seqFiles?: (ArrayBuffer | undefined)[] }

/** Teilmodell eines Körperteils nach dem Wert body (jedes Körperteil teilt ihn durch seine Basis) */
export function pickSubmodel(bp: MdlBodypart, body: number): MdlSubmodel | null {
  if (!bp.models.length) return null;
  return bp.models[Math.floor(Math.max(0, body) / Math.max(1, bp.base)) % bp.models.length] || bp.models[0];
}

/** Alle Dreiecke in der gewählten Stellung, nach Textur gruppiert. Koordinaten wie im Spiel (X vor, Y links, Z oben). */
export function buildGeometry(m: MdlFile, o: BuildOpts = {}): MdlGroup[] {
  const pose = bonePose(m, o.seq || 0, o.frame || 0, o.seqFiles);
  const fam = o.skin && o.skin > 0 && o.skin < m.skins.length ? o.skin : 0;
  const skin = m.skins[fam];
  const groups = new Map<number, MdlGroup>();
  const body = o.body || 0;
  for (const bp of m.bodyparts) {
    const sm = pickSubmodel(bp, body);
    if (!sm) continue;
    const nv = sm.vertBone.length, xv = new Float32Array(nv * 3);
    for (let i = 0; i < nv; i++) {
      const t = pose[sm.vertBone[i]]; if (!t) continue;
      const x = sm.verts[i * 3], y = sm.verts[i * 3 + 1], z = sm.verts[i * 3 + 2];
      xv[i * 3] = t[0] * x + t[1] * y + t[2] * z + t[3]; xv[i * 3 + 1] = t[4] * x + t[5] * y + t[6] * z + t[7]; xv[i * 3 + 2] = t[8] * x + t[9] * y + t[10] * z + t[11];
    }
    const nn = sm.normBone.length, xn = new Float32Array(nn * 3);
    for (let i = 0; i < nn; i++) {
      const t = pose[sm.normBone[i]]; if (!t) continue;
      const x = sm.norms[i * 3], y = sm.norms[i * 3 + 1], z = sm.norms[i * 3 + 2];
      xn[i * 3] = t[0] * x + t[1] * y + t[2] * z; xn[i * 3 + 1] = t[4] * x + t[5] * y + t[6] * z; xn[i * 3 + 2] = t[8] * x + t[9] * y + t[10] * z;
    }
    for (const mesh of sm.meshes) {
      const ti = skin ? skin[mesh.skinref] : mesh.skinref;
      const tex = m.textures[ti];
      const sw = tex && tex.w ? 1 / tex.w : 1, sh = tex && tex.h ? 1 / tex.h : 1;
      let g = groups.get(ti);
      if (!g) { g = { texture: ti, pos: [], nrm: [], uv: [] }; groups.set(ti, g); }
      const c = mesh.cmds;
      const vert = (p: number): void => {
        const vi = c[p], ni = c[p + 1];
        if (vi < 0 || vi >= nv) { g!.pos.push(0, 0, 0); g!.nrm.push(0, 0, 1); g!.uv.push(0, 0); return; }
        g!.pos.push(xv[vi * 3], xv[vi * 3 + 1], xv[vi * 3 + 2]);
        if (ni >= 0 && ni < nn) g!.nrm.push(xn[ni * 3], xn[ni * 3 + 1], xn[ni * 3 + 2]); else g!.nrm.push(0, 0, 1);
        g!.uv.push(c[p + 2] * sw, c[p + 3] * sh);
      };
      let p = 0;
      for (;;) {
        const n = c[p++];
        if (!n) break;
        const cnt = Math.abs(n), s = p;
        for (let k = 0; k + 2 < cnt; k++) {
          if (n < 0) { vert(s); vert(s + (k + 1) * 4); vert(s + (k + 2) * 4); }                       // Fächer
          else if (k % 2 === 0) { vert(s + k * 4); vert(s + (k + 1) * 4); vert(s + (k + 2) * 4); }   // Streifen
          else { vert(s + (k + 1) * 4); vert(s + k * 4); vert(s + (k + 2) * 4); }
        }
        p += cnt * 4;
      }
    }
  }
  return Array.from(groups.values());
}

/** Kleinste und größte Ecke aller Dreiecke (Mitte und Größe für Platzierung und Prüfung) */
export function geometryBounds(groups: MdlGroup[]): { min: number[]; max: number[] } | null {
  const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
  let any = false;
  for (const g of groups) for (let i = 0; i < g.pos.length; i += 3) for (let k = 0; k < 3; k++) { any = true; min[k] = Math.min(min[k], g.pos[i + k]); max[k] = Math.max(max[k], g.pos[i + k]); }
  return any ? { min, max } : null;
}

/** Name der Begleitdatei mit den Texturen: nameT.mdl (Groß-/Kleinschreibung offen) */
export const textureFileOf = (path: string): string => path.replace(/\.mdl$/i, 'T.mdl');
/** Name der Zusatzdatei einer Sequenzgruppe: name01.mdl für Gruppe 1 */
export const seqFileOf = (path: string, group: number): string => path.replace(/\.mdl$/i, String(group).padStart(2, '0') + '.mdl');
